import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Container, Typography, Grid, Card, CardContent, Button, Box } from '@mui/material';
import {
    Engineering,
    Architecture,
    Construction,
    Business,
    Warehouse,
    AccountTree,
    Build,
    Security,
    Speed,
    EmojiTransportation,
    Straighten,
    SquareFoot,
    RequestQuote
} from '@mui/icons-material';
import '../styles/Services.scss';

const Services = () => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const services = [
        {
            title: 'Szigeteléstechnika',
            description: 'Csarnokok, raktárak és gyártóüzemek komplett vízszigetelés kivitelezése. Modern technológiák és ipari szabványoknak megfelelő megoldások.',
            icon: <Warehouse sx={{ fontSize: 40 }} />,
            features: [
                'Padlózat víz és hőszigetelés',
                'PVC rendszerű tetők víz szigetelése',
                'Nehézlemez szigetelések'
            ],
            highlight: 'Több mint 50.000 m² megépített felület'
        },
        {
            title: 'Toronyépítés és Távközlés',
            description: 'Telekommunikációs tornyok és speciális építmények professzionális kivitelezése, a tervezéstől a kivitelezésig.',
            icon: <Architecture sx={{ fontSize: 40 }} />,
            features: [
                'Távközlési tornyok építése',
                'Antenna rendszerek telepítése',
                'Strukturális megerősítések',
                'Magasépítési szakmunkák'
            ],
            highlight: 'Országos lefedettségű projektek'
        },
        {
            title: 'Kereskedelmi Ingatlanok',
            description: 'Üzletközpontok, irodaházak és kereskedelmi épületek kivitelezése, modern építészeti megoldásokkal.',
            icon: <Business sx={{ fontSize: 40 }} />,
            features: [
                'Komplett épület kivitelezés',
                'Homlokzati rendszerek',
                'Beltéri kialakítások',
                'Falazatok festése',
                'Burkolatok készítése',
                'Parkettázás'
            ],
            highlight: 'Fenntartható építészeti megoldások'
        },
        {
            title: 'Projektmenedzsment',
            description: 'Professzionális projektvezetés, amely biztosítja a határidők betartását és a költséghatékony kivitelezést.',
            icon: <AccountTree sx={{ fontSize: 40 }} />,
            features: [
                'Teljes projektkoordináció',
                'Alvállalkozói menedzsment',
                'Minőségbiztosítás',
                'Dokumentáció kezelés'
            ],
            highlight: '100+ sikeresen lezárt projekt'
        },
        {
            title: 'Speciális Építmények',
            description: 'Egyedi igényekre szabott speciális építmények kivitelezése, különleges műszaki megoldásokkal.',
            icon: <Engineering sx={{ fontSize: 40 }} />,
            features: [
                'Egyedi acélszerkezetek',
                'Parkoló rendszerek kialakítása',
                'Megujuló energia beruházások',
                'Biztonsági rendszerek',
                'Ipari alpintechnikai tevékenységek'
            ],
            highlight: 'Innovatív mérnöki megoldások'
        },
        {
            title: 'Épületvillamosság',
            description: 'Meglévő tervek alapján végzett épületvillamossági kivitelezés és műszaki megvalósítás.',
            icon: <Build sx={{ fontSize: 40 }} />,
            features: [
                'Szabványosítás',
                'Gyorsszolgálat',
            ],
            highlight: 'Akkreditált szakértői háttér'
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };
    const foundingYear = 2005;
    const currentYear = new Date().getFullYear();
    const experienceYears = currentYear - foundingYear;
    return (
        <div className="services-page">
            <motion.div
                className="services-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                <Container>
                    <Typography variant="h1" className="page-title">
                        Szolgáltatásaink
                    </Typography>
                    <Typography variant="h2" className="page-subtitle">
                        Professzionális építőipari megoldások az alapoktól a kulcsátadásig
                    </Typography>
                    <div className="hero-features">
                        <div className="feature">
                            <Speed /> <span>{experienceYears}+ év tapasztalat</span>
                        </div>
                        <div className="feature">
                            <Security /> <span>Minősített kivitelezés</span>
                        </div>
                        <div className="feature">
                            <EmojiTransportation /> <span>Országos lefedettség</span>
                        </div>
                        
                    </div>
                </Container>
            </motion.div>

            <Container>
                <motion.div
                    ref={ref}
                    variants={containerVariants}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    className="services-grid"
                >
                    <Grid container spacing={4}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <motion.div variants={itemVariants}>
                                    <Card className="service-card">
                                        <CardContent>
                                            <div className="service-icon">
                                                {service.icon}
                                            </div>
                                            <Typography variant="h4" className="service-title">
                                                {service.title}
                                            </Typography>
                                            <Typography variant="body1" className="service-description">
                                                {service.description}
                                            </Typography>
                                            <div className="service-highlight">
                                                <Straighten sx={{ fontSize: 20, marginRight: 1 }} />
                                                {service.highlight}
                                            </div>
                                            <ul className="service-features">
                                                {service.features.map((feature, idx) => (
                                                    <motion.li
                                                        key={idx}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: 0.5 + idx * 0.1 }}
                                                    >
                                                        {feature}
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>

            <motion.section
                className="cta-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
            >
                <Container>
                    <Box className="cta-content">
                        <Typography variant="h2" className="cta-title">
                            Kérjen Ajánlatot Most!
                        </Typography>
                        <Typography variant="body1" className="cta-description">
                            A megkeresés és felkérés ugyanaz. Az árajánlat adása 40000 Ft + áfa. A munka elnyerésével ettől az összegtől eltekintünk.
                        </Typography>
                        <Button
                            variant="contained"
                            className="cta-button"
                            href="/kapcsolat"
                            startIcon={<RequestQuote />}
                        >
                            Ajánlatkérés
                        </Button>
                    </Box>
                </Container>
            </motion.section>
        </div>
    );
};

export default Services; 